import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import CartDrawer from './CartDrawer';
import { BrandLogo } from './BrandUI';
import { Toaster } from 'react-hot-toast';

const primaryLinks = [
  { to: '/', label: 'Home', end: true },
  { to: '/browse', label: 'Figures' },
  { to: '/summon', label: 'Summon' },
];

export default function Layout() {
  const { cartItems } = useCart();
  const { user, profile, signOut } = useAuth();
  const [showCartDrawer, setShowCartDrawer] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navLinkClass = ({ isActive }) =>
    `relative px-3 py-2 text-xs font-black uppercase tracking-[0.18em] transition ${
      isActive ? 'text-white after:scale-x-100' : 'text-slate-400 hover:text-white after:scale-x-0 hover:after:scale-x-100'
    } after:absolute after:inset-x-3 after:-bottom-0.5 after:h-0.5 after:origin-left after:bg-[#1683f3] after:shadow-[0_0_10px_#1683f3] after:transition-transform`;

  return (
    <div className="min-h-screen flex flex-col bg-[#0b1020] text-white">
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          style: {
            background: '#171D2B',
            color: '#F8FAFC',
            border: '1px solid rgba(22, 131, 243, 0.35)',
          },
        }}
      />

      <header className="sticky top-0 z-50 border-b border-blue-400/20 bg-[#0b1020]/95 shadow-[0_10px_35px_rgba(0,0,0,0.3)] backdrop-blur-xl">
        <nav className="mx-auto flex min-h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8" aria-label="Primary navigation">
          <div className="flex items-center gap-6 lg:gap-8">
            <BrandLogo />
            <div className="hidden items-center md:flex">
              {primaryLinks.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass}>
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>

          <div className="hidden items-center gap-3 md:flex">
            {user ? (
              <div className="flex items-center gap-3">
                <span className="max-w-36 truncate text-xs text-slate-400">
                  {profile?.display_name || user.email}
                </span>
                <button onClick={signOut} className="nfig-button-secondary min-h-9 px-3 py-1.5 text-[0.65rem]">
                  Logout
                </button>
              </div>
            ) : (
              <NavLink to="/login" className={navLinkClass}>Account</NavLink>
            )}
            <button
              type="button"
              onClick={() => setShowCartDrawer(true)}
              className="relative flex min-h-10 items-center gap-2 rounded-lg border border-blue-300/20 bg-blue-400/10 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] transition hover:border-blue-300/60 hover:bg-blue-400/20"
            >
              Cart
              <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-[#1683f3] px-1.5 text-[0.65rem] text-white">
                {cartCount}
              </span>
            </button>
          </div>

          <button
            type="button"
            className="flex h-11 w-11 flex-col items-center justify-center gap-1.5 rounded-lg border border-blue-300/20 bg-white/5 md:hidden"
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className={`h-0.5 w-5 bg-white transition ${menuOpen ? 'translate-y-2 rotate-45' : ''}`} />
            <span className={`h-0.5 w-5 bg-white transition ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`h-0.5 w-5 bg-white transition ${menuOpen ? '-translate-y-2 -rotate-45' : ''}`} />
          </button>
        </nav>

        {menuOpen && (
          <div className="border-t border-blue-400/15 bg-[#0b1020] px-4 pb-5 pt-3 md:hidden">
            <div className="mx-auto grid max-w-7xl gap-1">
              {primaryLinks.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass}>
                  {item.label}
                </NavLink>
              ))}
              <div className="mt-3 flex items-center gap-3 border-t border-white/10 pt-4">
                {user ? (
                  <button onClick={signOut} className="nfig-button-secondary flex-1">Logout</button>
                ) : (
                  <Link to="/login" className="nfig-button-secondary flex-1">Account</Link>
                )}
                <button onClick={() => setShowCartDrawer(true)} className="nfig-button-primary flex-1">
                  Cart <span className="rounded-full bg-white/20 px-2">{cartCount}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <div className="flex-grow">
        <Outlet />
      </div>

      <footer className="border-t border-blue-400/15 bg-[#080c18] px-4 py-8 text-sm text-slate-500">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo compact />
            <div>
              <p className="font-black uppercase tracking-[0.18em] text-slate-200">NFig</p>
              <p className="text-xs">Figures. Fandom. Fortune.</p>
            </div>
          </div>
          <nav className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy" className="transition hover:text-blue-300">מדיניות פרטיות</Link>
            <Link to="/terms" className="transition hover:text-blue-300">תנאי שימוש</Link>
            <Link to="/returns" className="transition hover:text-blue-300">החזרות וביטולים</Link>
            <Link to="/shipping" className="transition hover:text-blue-300">מדיניות משלוחים</Link>
          </nav>
          <span className="text-xs">© {new Date().getFullYear()} NFig</span>
        </div>
      </footer>

      <CartDrawer isOpen={showCartDrawer} onClose={() => setShowCartDrawer(false)} />
    </div>
  );
}
